export type FieldType = "text" | "string-list" | "select" | "date" | "month-year" | "bool" | "skill";

export interface FieldOption {
  value: string;
  label: string;
  section?: string;
  /** Optional one-char prefix rendered before the label (e.g. "★" for rollup entries). */
  glyph?: string;
}

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: FieldOption[];
  /** Uses the catalogue-specific grouped/searchable degree control. */
  selectKind?: "degree";
  placeholder?: string;
  required?: boolean;
  hideLabel?: boolean;
  prominent?: boolean;
  /**
   * Optional DOM id stamped on this field's input by EntryFields — a stable,
   * locale-independent hook for e2e tests (spark-validation), mirroring
   * `Select.id`. month-year fields derive `${id}-month` / `${id}-year`.
   */
  id?: string;
  /**
   * Opts a `string-list` field into AI-backed suggestions. "activity" wires
   * the field to `suggestActivityDescriptions`, driven by the sibling
   * `jobTitle` / `department` values of the same entry.
   */
  suggest?: "activity";
  /**
   * When true, EntryFields renders this field and the immediately-following
   * field side by side in a 2-column grid at `sm:` breakpoint and up. Both
   * paired fields should be roughly short and same-type for the layout to
   * read cleanly (e.g. Arbeitgeber + Abteilung, Land + Ort).
   */
  pairWithNext?: boolean;
  /**
   * Conditionally required based on sibling values in the same entry (e.g. a
   * foreign language needs a CEFR level, a native speaker doesn't). Evaluated
   * by `isFieldMissingInEntry`; the value-only `isFieldMissing` ignores it.
   */
  requiredWhen?: (entry: Record<string, unknown>) => boolean;
}

// A field is required only when explicitly marked. Default = optional.
export const isRequired = (f: FieldDef): boolean => f.required === true;

// Empty/invalid check for a field's value, independent of whether it's required.
//
// Month-year fields carry a wire-format quirk: when the user picks a month
// before a year, MonthYearSelect writes a sentinel "-MM" so the dropdown
// doesn't snap back. The sentinel is non-empty but invalid; genesis rejects
// it on save. The regex below treats it as missing so the user sees the
// PFLICHTFELD tooltip and fills the year before the row leaves the panel.
function isEmptyValue(f: FieldDef, value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "boolean") return false;
  const s = String(value).trim();
  if (s === "") return true;
  return f.type === "month-year" && !/^\d{4}(-\d{2}(-\d{2})?)?$/.test(s);
}

// Centralised "is this required field still empty?" predicate. Used by every
// form that gates a confirm button on PFLICHTFELD-ness so the rules stay
// consistent across the edit panels (rows.tsx, CareerGraph.tsx) and the add
// flow (AddForm.tsx). Non-required fields always pass.
export function isFieldMissing(f: FieldDef, value: unknown): boolean {
  return isRequired(f) && isEmptyValue(f, value);
}

// Entry-aware variant: also honours `requiredWhen(entry)`, so a field can be
// required based on a sibling value (e.g. cefr required unless nativeSpeaker).
// Pass the whole entry/form so the predicate can read its siblings.
export function isFieldMissingInEntry(f: FieldDef, entry: Record<string, unknown>): boolean {
  const required = isRequired(f) || (f.requiredWhen?.(entry) ?? false);
  return required && isEmptyValue(f, entry[f.key]);
}
